import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UsersManagementService } from './usersManagement.service';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private authUrl = environment.authentication;

  private roleSubject = new BehaviorSubject<String | null>(null);
  private userDataSubject = new BehaviorSubject<any>(null);
  private userDataLoadedSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private usersManagementService: UsersManagementService,
    private http: HttpClient,
    private router: Router,
    private permissionService: PermissionService
  ) {
    //const session = sessionStorage.getItem('session');
    //if (session) {
    //  this.loadUserRole(session);
    //}
    const token = JSON.parse(sessionStorage.getItem('authToken') as string)?.token;
    if (token) {
      this.loadUserRole();
    }
  }

  //// Chama a API para carregar os dados do utilizador, incluindo a role
  private loadUserRole(): void {
    this.userDataLoadedSubject.next(false); // Marca como "a carregar"
    this.usersManagementService.getMe().pipe(
      catchError(error => {
        // Trata erros da API e garante que o fluxo continua
        this.roleSubject.next(null);
        this.userDataSubject.next(null);
        this.userDataLoadedSubject.next(true); // Marca como carregado
        return [];
      })
    ).subscribe(data => {
      const previousRole = this.userDataSubject.value?.role ?? null;

      if (data && data.role) {
        const user = data;

        this.userDataSubject.next(user); // Guarda os dados do utilizador
        this.roleSubject.next(user.role); // Guarda a role do utilizador
        this.permissionService.setCurrentUserId(data.id);
      } else {
        this.roleSubject.next(null);
        this.userDataSubject.next(null);
      }
      this.userDataLoadedSubject.next(true); // Marca como carregado
    });

  }

  get userDataLoaded$(): Observable<boolean> {
    return this.userDataLoadedSubject.asObservable();
  }

  // Função para verificar se é admin
  isAdmin(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "ADMIN" || role === "SUPER-ADMIN") // Retorna true se for admin
    );
  }

  isAdministrator(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "ADMIN") // Retorna true se for admin
    );
  }


  isArchitect(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "ARCHITECT") // Retorna true se for admin
    );
  }

  isSuperAdminOrArchitect(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "ARCHITECT" || role === "SUPER-ADMIN") // Retorna true se for admin
    );
  }

  // Função para verificar se é admin
  isSuperAdmin(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "SUPER-ADMIN") // Retorna true se for admin
    );
  }

  // Função para verificar se é utilizador normal
  isUser(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "USER") // Retorna true se for utilizador normal
    );
  }

  // Função para verificar se é backoffice
  isEstimationOffice(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "ESTIMATION-OFFICE") // Retorna true se for utilizador normal
    );
  }

  // Função para verificar se é backoffice or super-admin
  isEstimationOfficeOrSuperAdmin(): Observable<boolean> {
    return this.roleSubject.asObservable().pipe(
      filter(role => role !== null), // Espera até ter um valor válido
      map(role => role === "ESTIMATION-OFFICE" || role === "SUPER-ADMIN") // Retorna true se for backoffice ou super admin
    );
  }

  logOut() {
    /* let session = sessionStorage.getItem("session");
    let sessionName = sessionStorage.getItem("sessionName"); */
    sessionStorage.removeItem('session');
    sessionStorage.removeItem('sessionName');
  }

  isAuthenticated(): boolean {
    return this.getToken() ? true : false;
  }

  sendLoginData(email: string, password: string): Observable<any> {
    const body = {
      email: email,
      password: password
    };

    return this.http.post<any>(this.authUrl + "/login", body);
  }


  setToken(token: string, name: string, expirationTime: number) {
    const expiryDate = new Date().getTime() + expirationTime;

    const tokenData = {
      token: token,
      name: name,
      expiry: expiryDate
    };
    sessionStorage.setItem('authToken', JSON.stringify(tokenData));

    this.loadUserRole();
    this.permissionService.loadPermissions().subscribe();
  }


  getToken() {
    const tokenData = JSON.parse(sessionStorage.getItem('authToken') as string);

    if (tokenData) {
      const currentTime = new Date().getTime();

      if (currentTime < tokenData.expiry) {
        return tokenData.token;
      } else {
        this.clearToken();
      }

    }
    return null;
  }

  getUserData(): Observable<any> {
    return this.userDataSubject.asObservable().pipe();
  }

  refreshUserData(): void {
    this.loadUserRole();
  }

  returnUserName(): string[] | null {
    const tokenData = JSON.parse(sessionStorage.getItem('authToken') as string);

    if (tokenData) {
      const currentTime = new Date().getTime();

      if (currentTime < tokenData.expiry) {
        return tokenData.name;
      } else {
        this.clearToken();
      }

    }

    return null;
  }


  returnExpireDate(): number {
    const tokenData = JSON.parse(sessionStorage.getItem('authToken') as string);

    if (tokenData) {
      const currentTime = new Date().getTime();

      if (currentTime < tokenData.expiry) {
        return tokenData.expiry;
      } else {
        this.clearToken();
      }

    }
    return 0;
  }


  // Function to clear the token
  clearToken() {
    this.permissionService.clear();
    sessionStorage.removeItem('authToken'); // Remove token from session storage
    this.logOut();
  }

}
